<template>
    <app-layout>
        <template #header>
            <AppHead :title="`Customer ${form.first_name} ${form.last_name}`" v-if="form.last_name" />
            <AppHead title="Customer" v-else />
            <h2 class="title">
                <span v-if="form.first_name"> Customer {{ form.first_name }} {{ form.last_name }}
                </span>
                <span v-else-if="user.status_code === 'guest'">Guest</span>
                <span v-else-if="user.status_code === 'invited'">Guest</span>
            </h2>
            <flash-messages />
        </template>

        <div class="row mt-4">
            <div class="col-12 col-lg-6">
                <card title="Customer Information" icon="fas fa-user" headerStyle="dark">
                    <ul class="list-unstyled">
                        <li>
                            <strong>Name: </strong>
                            <p v-if="user.first_name" class="mb-0 d-inline-block">{{ user.first_name }} {{ user.last_name }}
                            </p>
                            <p v-else-if="user.status_code === 'guest'" class="mb-0 d-inline-block">Guest</p>
                            <p v-else-if="user.status_code === 'invited'" class="mb-0 d-inline-block">Invited</p>
                        </li>
                        <li v-if="balance"><strong>Account Balance:</strong> {{ (balance < 0 ? '-$' : '$') +
                            formatCurrency(Math.abs(balance)) }}</li>
                        <li>
                            <strong>Last Payment: </strong>
                            <inertia-link v-if="user.last_payment" :href="route('tu.payment.receipt', user.last_payment)"
                                tabindex="-1">
                                {{ formatDollars(user.last_payment.amount) }} on {{
                                    moment(user.last_payment.transaction_date).format('M/D/YY h:mm A') }}
                            </inertia-link>
                            <p v-else class="mb-0 d-inline-block">No Payments</p>
                        </li>
                        <li v-if="user.email"><strong>Email:</strong>
                            <Email v-model="user.email" />
                        </li>
                        <li v-if="user.phone_number"><strong>Phone:</strong>
                            <Phone v-model="user.phone_number" />
                        </li>
                    </ul>

                    <hr>

                    <div v-if="hasLocation" class="mb-3">
                        <p class="mb-0"><i class="fas fa-map-marker-alt mr-2 text-primary"></i>Location</p>
                        <p class="mb-0 text-secondary">{{ location.company_name }} {{ location.address1 }} {{
                            location.address2 }} {{ location.city }}, {{ location.state }} {{ location.postcode }}</p>
                    </div>

                    <div>
                        <p class="mb-0">Tax Exempt Status:</p>
                        <p v-if="!file && !user.tax_exempt" class="text-secondary">Not Requested</p>
                        <div v-else-if="!user.tax_exempt">
                            Pending Approval -
                            <a :href="file.url" target="_new">Uploaded {{ moment(file.date).format('M/D/YY h:mm A') }}</a>

                            <br /><br />

                            <!-- tax exempt form -->
                            <div class="form-group">
                                <!-- Profile Photo File Input -->
                                <input type="file" hidden ref="document" @change="uploadDocument">

                                <jet-button class="mt-2 mr-2" icon="fas fa-file" @click.prevent="selectDocument">
                                    Upload Document
                                </jet-button>
                                <jet-input-error :message="formUpload.errors.document" class="mt-2" />
                            </div>

                            <jet-button @click="setTaxExempt()" v-if="!user.tax_exempt">Approve Tax Exempt</jet-button>
                        </div>
                        <p v-else class="text-secondary">Approved Tax Exempt</p>
                    </div>

                </card>

                <card class="mt-3" title="Store Credit">
                    <p class="mb-0 text-secondary">Current: <span class="text-success">{{
                        formatDollars(user.available_credit) }}</span></p>

                    <div class="form-group mt-3">
                        <jet-label for="credit_amount" value="Add Store Credit" />
                        <AmountInput inputId="credit_amount" v-model="formStoreCredit.amount" />
                        <jet-input-error :message="formStoreCredit.errors.amount" class="mt-2" />

                        <jet-label for="credit_note" value="note" />
                        <jet-input inputId="credit_note" v-model="formStoreCredit.note" />
                        <jet-input-error :message="formStoreCredit.errors.note" class="mt-2" />
                    </div>

                    <hr>

                    <div class="form-check">
                        <input type="checkbox" class="form-check-input" id="store_credit_notify"
                            v-model="formStoreCredit.store_credit_notify" />
                        <label for="store_credit_notify">Notify Customer</label>
                    </div>

                    <jet-button icon="fas fa-plus" :disable="formStoreCredit.processing"
                        @click="addStoreCredit">Add</jet-button>
                </card>
            </div>
            <div class="col-12 col-lg-4 mt-3 mt-lg-0">
                <jet-button class="w-100" :class="{ disabled: !balance }" :goesTo="route('tu.balancepayment', user)"
                    icon="fas fa-dollar-sign">Make Payment</jet-button>
                <jet-button class="w-100 mt-2" :goesTo="route('tu.edit', user)" icon="fas fa-user">Edit
                    Customer</jet-button>
                <jet-button class="mt-2 w-100" :goesTo="route('tu.terms', user)">Manage Payment Settings</jet-button>
                <jet-button v-if="user.pending_invite" :disable="invitationProcessing" @click="resendInvitation(user)"
                    class="mt-2 w-100" icon="fas fa-envelope">Resend Invitation</jet-button>
                <jet-button v-if="!user.pending_invite" class="w-100 mt-2" @click="sendReset">
                    Send Password Reset
                </jet-button>

                <card title="Account Statement" class="mb-3 mt-4">
                    <div class="row">
                        <div class="col-12">
                            <jet-label for="start_date" value="Select Start Date" />
                            <Calendar class="w-100" v-model="start_date" dateFormat="mm/dd/yy" show-icon="true" />
                        </div>

                        <div class="col-12">
                            <jet-label for="end_date" value="Select End Date" />
                            <Calendar class="w-100" v-model="end_date" dateFormat="mm/dd/yy" show-icon="true" />
                        </div>

                        <div class="col-12 bottom-button text-center w-100 mt-2">
                            <jet-button class="mt-3 mb-3 w-100" icon="fas fa-file-invoice" btnStyle="success"
                                @click="openPrintAccountActivity">Print</jet-button>
                        </div>
                    </div>
                </card>
            </div>
        </div>

        <section v-if="orders.length" class="mt-3">
            <h3 class="mb-2">Orders</h3>
            <DataTable :value="orders" class="p-datatable" tableClass="border-left border-right border-bottom"
                responsiveLayout="stack" breakpoint="992px" stripedRows :paginator="true" :rows="10"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                :rowClass="() => { return 'table-row' }"
                @row-click="(e) => $inertia.get(route('mtd.order.view', e.data.id))">
                <Column field="id" header="ID">
                    <template #body="slotProps">
                        {{ slotProps.data.id }}
                        <PaymentMethodDisplay :payment_method="slotProps.data.payment.payment_method"
                            :classes="'text-secondary text-size-small'" />
                    </template>
                </Column>
                <Column field="status_desc" header="Status"></Column>
                <Column class="width-third" field="total" header="Total">
                    <template #body="slotProps">
                        {{ formatDollars(slotProps.data.total) }}
                    </template>
                </Column>
                <Column field="date" header="Date">
                    <template #body="slotProps">
                        {{ moment(slotProps.data.date).format('M/D/YY h:mm A') }}
                    </template>
                </Column>
                <Column field="id" header="">
                    <template #body="slotProps">
                        <div class="d-flex justify-content-end mr-2">
                            <jet-button :goesTo="route('mtd.order.view', slotProps.data.id)" class="btn-sm"
                                icon="fas fa-angle-right" tabindex="-1">View</jet-button>
                        </div>
                    </template>
                </Column>
            </DataTable>
        </section>

        <section v-if="payments.length" class="mt-3">
            <h3 class="mb-2">Payment History</h3>
            <DataTable :value="payments" class="p-datatable" tableClass="border-left border-right border-bottom"
                responsiveLayout="stack" breakpoint="992px" stripedRows :paginator="true" :rows="10"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                @row-click="(e) => openPrintPaymentReceipt(e.data.id)" :rowClass="paymentRowClass">
                <Column field="id" header="ID"></Column>
                <Column field="description" header="Type">
                    <template #body="slotProps">
                        <label v-if="slotProps.data.payment_method?.cc_image_url">
                            <img :src="slotProps.data.payment_method.cc_image_url"
                                alt="{{ slotProps.data.payment_method.brand }}" width="32" />
                            <span class="text-secondary text-size-small"> ending in {{ slotProps.data.payment_method.last4
                            }}</span>
                        </label>
                        <span v-else>{{ slotProps.data.description }}</span>
                    </template>
                </Column>
                <Column field="transaction_date" header="Date">
                    <template #body="slotProps">
                        {{ moment(slotProps.data.transaction_date).format('M/D/YY h:mm A') }}
                    </template>
                </Column>
                <Column field="total_amount" header="Amount">
                    <template #body="slotProps">
                        {{ formatDollars(slotProps.data.total) }}
                    </template>
                </Column>
            </DataTable>
        </section>

        <Notes @addNote="addNote" :notes="notes" :formNote="formNote" formPlaceholder="Add note on this account">
            <div class="form-group form-check mb-1">
                <input type="checkbox" v-model="formNote.visible_tu" class="form-check-input" true-value="1"
                    false-value="0" />
                <label class="form-check-label">Visible to Customer</label>
            </div>
            <div v-if="formNote.visible_tu" class="form-group form-check">
                <input type="checkbox" v-model="formNote.notify_tu" class="form-check-input" true-value="1"
                    false-value="0" />
                <label class="form-check-label">Notify Customer</label>
            </div>
        </Notes>
    </app-layout>
    <teleport to="body">
        <PrintModal :printUrl="printUrl" @print="print" />
    </teleport>
</template>

<script>
import AmountInput from '@/Shared/AmountInput'
import AppLayout from '@/Layouts/AppLayout'
import Calendar from "primevue/calendar"
import FileInput from '@/Shared/FileInput'
import FlashMessages from '@/Shared/FlashMessages'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import moment from 'moment-timezone'
import Notes from '@/Shared/Notes'
import PaymentMethodDisplay from '@/Components/Transaction/PaymentMethodDisplay'
import PrintModal from '@/Components/Modals/PrintModal'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'

export default {
    components: {
        AppLayout,
        Calendar,
        FlashMessages,
        FileInput,
        SelectInput,
        TextInput,
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetCheckbox,
        JetInputError,
        JetLabel,
        AmountInput,
        Notes,
        PaymentMethodDisplay,
        PrintModal,
    },
    remember: 'form',
    props: {
        user: Object,
        balance: Number,
        location: Object,
        locations: Array,
        orders: Array,
        payments: Array,
        notes: Array,
        file: Object
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: 'put',
                company_name: this.user.company_name,
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                location_id: this.user.location_id,
                address1: null,
                address2: null,
                city: null,
                state: null,
                postcode: null
            }),
            formPassword: this.$inertia.form({
                email: this.user.email
            }),
            formStoreCredit: this.$inertia.form({
                amount: null,
                note: null,
                store_credit_notify: true
            }),
            formUpload: this.$inertia.form({
                _method: 'PUT',
                document: null,
            }),
            formNote: this.$inertia.form({
                _method: 'put',
                content: null,
                visible_tu: 0,
                notify_tu: 0
            }),
            accountActivityProcessing: false,
            invitationProcessing: false,
            selectedReceiptPaymentId: null,
            printReport: '',
            printUrl: '',
            start_date: moment().date(1).month('January').format('M/D/Y'),
            end_date: moment().endOf('month').format('M/D/Y'),
        }
    },
    created() {
        this.moment = moment;
    },
    computed: {
        hasLocation() {
            const { location } = this

            return location.company_name || location.address1 || location.address2 || location.city || location.state || location.postcode
        },
    },
    methods: {
        addStoreCredit() {
            this.formStoreCredit.post(this.route('tu.user.addstorecredit', this.user.id), {
                onSuccess: () => this.formStoreCredit.reset('amount', 'store_credit_notify'),
            });
        },
        sendReset() {
            this.$inertia.visit(this.route('mtd.users.resetlink', this.user.id));
        },
        addNote() {
            this.formNote.post(this.route('tu.addnote', this.user.id), {
                preserveScroll: true,
                onSuccess: () => this.formNote.reset('content', 'visible_tu', 'notify_tu'),
            })
        },
        setTaxExempt() {
            this.$inertia.put(this.route('tu.taxexempt', this.user.id))
        },
        resendInvitation(user) {
            this.invitationProcessing = true
            this.$inertia.visit(this.route('tu.resend', user), {
                onSuccess: () => this.invitationProcessing = false,
                onFinish: () => this.invitationProcessing = false,
                preserveScroll: true
            })
        },
        uploadDocument() {
            if (this.$refs.document) {
                this.formUpload.document = this.$refs.document.files[0]

                this.formUpload.post(route('user.uploadtaxexempt', this.user), {
                    errorBag: 'updateProfileInformation',
                    preserveScroll: true
                });
            }
        },
        selectDocument() {
            this.$refs.document.click();
        },
        paymentRowClass(data) {
            if (moment(data.date).isBefore(this.currentDate, 'day')) return 'payment-past table-row'
            if (moment(data.date).isSame(this.currentDate, 'day')) return 'payment-current table-row'
        },
        openPrintPaymentReceipt(paymentId) {
            this.printReport = 'tu.payment.receipt'
            this.selectedReceiptPaymentId = paymentId
            this.openPrintDialog();  
        },
        openPrintAccountActivity(){
            this.printReport = 'tu.report.accountActivity'
            this.openPrintDialog();  
        },
        openPrintDialog() {
            this.setPrintUrl(0);
            $('#PrintModal').modal('show');
        },
        print(){
            this.setPrintUrl(1);
        },
        setPrintUrl(print){
            switch(this.printReport) {
            case 'tu.payment.receipt':
                this.printUrl = this.route('tu.payment.receipt', { payment: this.selectedReceiptPaymentId, print: print });
                break;
            case 'tu.report.accountActivity':
                this.printUrl = this.route('tu.report.accountActivity',
                { user: this.user.id, start: this.dayStartUTC(this.start_date), end: this.dayEndUTC(this.end_date), utcOffset: moment().utcOffset() , print: print });
                break;
            }
        },
        dayStartUTC(date) {
            return moment.utc(moment(date).startOf('day')).unix();
        },
        dayEndUTC(date) {
            return moment.utc(moment(date).endOf('day')).unix();
        },
    },
}
</script>
